import { FormControl, TextField, useTheme } from "@mui/material";
import React from "react";

/**
 * A controlled text input field component without react-hook-form dependencies.
 * Supports single-line or multi-line input, regex-based input filtering,
 * and displays error state and helper text if provided.
 */
export interface ControlledTextFormFieldProps {
  /**
   * The current value of the input.
   */
  value: string;
  /**
   * Callback invoked when the input value changes.
   */
  onChange: (value: string) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => void;
  /**
   * Whether the field is disabled.
   */
  disabled?: boolean;
  /**
   * Whether the field is multi-line.
   */
  multiline?: boolean;
  /**
   * Number of visible text lines for a multi-line field.
   */
  rows?: number;
  /**
   * Minimum number of text rows for auto-sizing multi-line.
   */
  minRows?: number;
  /**
   * Maximum number of text rows for auto-sizing multi-line.
   */
  maxRows?: number;
  /**
   * Optional regex to validate or filter the input.
   * If provided, user input that doesn't match will be ignored.
   */
  valueRegex?: RegExp;
  /**
   * If true, the input will be styled as having an error.
   */
  error?: boolean;
  /**
   * Helper text to show beneath the input (e.g. validation message).
   */
  helperText?: string;
  /**
   * The label to display above the input field.
   */
  label?: string;
  /**
   * The placeholder text to display inside the input field.
   */
  placeholder?: string;
  isNumber?: boolean;
  key?: string;
}

export function ControlledTextFormField({
  value,
  onChange,
  onBlur,
  disabled = false,
  multiline = false,
  rows,
  minRows,
  maxRows,
  valueRegex,
  error = false,
  helperText,
  label,
  placeholder,
  isNumber,
  key,
}: Readonly<ControlledTextFormFieldProps>) {
  /**
   * Handle change events with optional regex filtering.
   */
  const theme = useTheme();
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    if (valueRegex && newValue !== "" && !valueRegex.test(newValue)) {
      // Do not propagate invalid input
      return;
    }
    onChange(newValue);
  };

  return (
    <FormControl fullWidth sx={{ height: "100%", width: "100%" }}>
      <TextField
        onBlur={onBlur}
        key={key}
        value={value}
        onChange={handleChange}
        multiline={multiline}
        disabled={disabled}
        fullWidth
        label={label}
        placeholder={placeholder}
        type={isNumber ? "number" : "text"}
        slotProps={{
          input: {
            style: {
              borderRadius: "6px",
              height: "45px",
            },
          },
        }}
        sx={{
          // single-line input
          "& .MuiInputBase-input": {
            fontSize: "14px",
          },
          // multiline textarea
          "& .MuiInputBase-inputMultiline, & textarea": {
            fontSize: "14px",
          },
          // label size (optional)
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          fieldset: {
            borderRadius: "6px",
            borderColor: error
              ? "#d32f2f !important"
              : theme.palette.text.primary,
          },
        }}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        error={error}
        helperText={helperText}
      />
    </FormControl>
  );
}
